import React, { useState, useRef, useEffect } from 'react';
import { Button, TextField } from '@mui/material';
import styles from './VerificationCode.module.scss';
import { useTranslation } from 'react-i18next';
import { CODE_LENGTH, sanitizeCode } from '../../utils/verificationCode';

export const VerificationCodeInput = ({ onSubmit, onResend, showResendButton, errorMessage }) => {
  const { t } = useTranslation();
  const [code, setCode] = useState(Array(CODE_LENGTH).fill(''));
  const inputsRef = useRef([]);
  const formRef = useRef(null);
  const [resendTimer, setResendTimer] = useState(120);
  const [isResendDisabled, setIsResendDisabled] = useState(true);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    if (value.length > 1) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < CODE_LENGTH - 1) {
      inputsRef.current[index + 1].focus();
    } else if (value && index === CODE_LENGTH - 1) {
      handleSubmit(newCode);
    }
  };

  const handlePaste = (e) => {
    const pasted = sanitizeCode(e.clipboardData.getData('text'));
    if (!pasted) return;
    e.preventDefault();

    const newCode = Array(CODE_LENGTH).fill('').map((_, i) => pasted[i] || '');
    setCode(newCode);

    if (pasted.length === CODE_LENGTH) {
      inputsRef.current[CODE_LENGTH - 1].focus();
      handleSubmit(newCode);
    } else {
      inputsRef.current[pasted.length].focus();
    }
  };

  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      setIsResendDisabled(true);
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setIsResendDisabled(false);
      if (interval) clearInterval(interval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendTimer]);
  
  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
    if (e.key === 'Enter' && !code.some(digit => !digit)) {
      handleSubmit();
    }
  };

  const handleSubmit = (currentCode = code) => {
    if (currentCode.some(digit => !digit)) return;
    const verificationCode = currentCode.join('');
    onSubmit(verificationCode);
  };

  useEffect(() => {
    if (inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
  }, []);

  const handleResendClick = () => {
    setResendTimer(120);
    onResend();
  };

  return (
    <div 
      className={styles.emailConfirmationContainer}
      ref={formRef}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !code.some(digit => !digit)) {
          handleSubmit();
        }
      }}
    >
      <h2>{t('emailConfirmation.title')}</h2>
      <p>{t('emailConfirmation.enterCode')}</p>
      <div className={styles.codeInputContainer}>
        {code.map((digit, index) => (
          <TextField
            key={index}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            inputRef={(el) => (inputsRef.current[index] = el)}
            variant="outlined"
            margin="normal"
            className={styles.codeInput}
            inputProps={{ 
              maxLength: 1,
              inputMode: 'numeric',
              pattern: '[0-9]*'
            }}
            sx={{
              width: { xs: 38, sm: 50 },
              height: { xs: 38, sm: 50 },
              '& .MuiInputBase-input': {
                fontSize: { xs: '18px', sm: '24px' },
                padding: { xs: '6px', sm: '12px' },
                textAlign: 'center'
              }
            }}
          />
        ))}
      </div>
      {errorMessage && <p style={{ color: '#d32f2f' }}>{errorMessage}</p>}
      {resendTimer > 0 && <p>{`${t('sms.resendCodeIn')} ${resendTimer} сек.`}</p>}
      <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '10px'}}>
        {showResendButton && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleResendClick}
            disabled={isResendDisabled}
            // sx={{
            //   width: { xs: '100%', sm: 'auto' },
            //   maxWidth: { xs: '280px', sm: 'none' },
            //   padding: { xs: '8px 16px', sm: '10px 24px' },
            //   marginTop: { xs: '8px', sm: '10px' }
            // }}
          >
            {t('emailConfirmation.resendCode')}
          </Button>
        )}
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => handleSubmit()}
          disabled={code.some(digit => !digit)}
          sx={{
            // width: { xs: '100%', sm: 'auto' },
            // maxWidth: { xs: '280px', sm: 'none' },
            // padding: { xs: '8px 16px', sm: '10px 24px' }
            marginLeft: '15px'
          }}
        >
          {t('sms.confirmCode')}
        </Button>
      </div>
      
    </div>
  );
};
